import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, X, Check, AlertTriangle, Download, RefreshCw } from '../../../icons';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import {
  fetchAiCatalog, saveAiCatalog, importDetectedProviders, probeAiModels,
  type AiEntry, type AiEntryDraft, type AiSurface, type AiEntryKind, type AiCatalogResult,
  type AiUsageRow,
} from '../../../api/aiProviders';

/**
 * 관리 콘솔 › AI › **AI 연결** (판 H-② · 판 J 개정 2026-09-10).
 *
 * ## 한 항목이 세 가지를 담는다
 *
 *   ① **연결** — 어디에 어떻게 붙나(API 주소·토큰·모델 / 실행 파일·모델)
 *   ② **공개** — 회사가 고를 수 있나, 어느 자리(AI 메뉴·챗)에서
 *   ③ **사용** — 최근에 얼마나 썼나 (판 J)
 *
 * ## ★★★ 여기가 «주인»이다 (판 J · 사용자 지시)
 *
 * 판 I 에는 「기존 provider 를 **가리키기**(ref)」가 있었고, 그 줄은 주소·토큰 칸이 없어
 * **콘솔에서 고칠 수가 없었다.** 「관리 콘솔은 시스템 관리자가 다 등록 및 수정까지 해야 한다」
 * 는 지시에 어긋나므로 걷어냈다. 이제 종류는 **주소·토큰을 직접 갖는 `env`** 와 **`cli`** 둘뿐이고,
 * 「기존 provider 가져오기」는 가리키는 대신 **값을 실제로 받아 담는다**(서버가 한다 — 토큰이
 * 화면을 거치지 않는다).
 *
 * ## ★★★ 열려면 «자리»를 골라야 한다
 *
 * 회사 목록은 `enabled && surfaces.includes(자리)` 로 걸러진다. 자리를 안 고른 채 열면
 * **콘솔은 「열림」인데 회사 화면에는 고를 것이 없다** — 운영에서 실제로 그 상태가 됐다.
 * 그래서 서버가 저장을 막고, 화면도 미리 말린다.
 *
 * ## 규격
 *
 * 콘솔 목록 공통 규격 — **화면 머리 → 툴바 → 본문**, 좌 분류(형 B), 수정은 **줄 끝 아이콘**.
 * ⚠ 경로(breadcrumb)는 안 그린다 — 셸이 이미 그린다.
 *
 * ## ★★★ 토큰은 «원문이 오지 않는다»
 *
 * 서버는 있음/없음과 꼬리 넉 자만 준다. 팝업은 **비워 두면 「그대로 둔다」**로 읽고 적었을 때만
 * 보낸다. 이 규칙이 없으면 **이름 한 글자를 고칠 때마다 토큰이 지워진다.**
 */

const SURFACE_LABEL: Record<AiSurface, string> = { 'ai-menu': 'AI 메뉴', chat: '챗' };
const KIND_LABEL: Record<AiEntryKind, string> = { env: 'API 연동', cli: '명령줄(CLI)' };
const ALL_SURFACES: AiSurface[] = ['ai-menu', 'chat'];

type SideKey = 'all' | AiEntryKind;

/** 큰 수를 읽기 쉽게 — 12,345 / 1.2M. */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return n.toLocaleString('ko-KR');
}

/** 팝업이 다루는 값 — 토큰은 «적었을 때만» 실어 보낸다. */
interface Draft {
  id?: string;
  kind: AiEntryKind;
  label: string;
  baseUrl: string;
  apiKey: string;
  /** 저장된 토큰을 그대로 둘 것인가. 끄면 지운다. */
  keepKey: boolean;
  hasApiKey: boolean;
  apiKeyHint: string | null;
  bin: string;
  model: string;
  surfaces: AiSurface[];
  enabled: boolean;
}

function toDraft(e: AiEntry | null): Draft {
  if (e === null) {
    return {
      kind: 'env', label: '', baseUrl: '', apiKey: '', keepKey: false,
      hasApiKey: false, apiKeyHint: null, bin: '', model: '', surfaces: [], enabled: false,
    };
  }
  return {
    id: e.id, kind: e.kind, label: e.label, baseUrl: e.baseUrl, apiKey: '',
    keepKey: e.hasApiKey, hasApiKey: e.hasApiKey, apiKeyHint: e.apiKeyHint,
    bin: e.bin, model: e.model, surfaces: e.surfaces, enabled: e.enabled,
  };
}

/** 손대지 않는 줄 — **토큰을 안 실어 보낸다**(서버가 예전 것을 지킨다). */
function untouched(e: AiEntry): AiEntryDraft {
  return {
    id: e.id, kind: e.kind, label: e.label, baseUrl: e.baseUrl, bin: e.bin,
    model: e.model, surfaces: e.surfaces, enabled: e.enabled,
  };
}

/** 팝업 값 → 저장 모양. ★ 토큰은 «적었거나 지우기로 했을 때»만 넣는다. */
export function toPayload(d: Draft): AiEntryDraft {
  const base: AiEntryDraft = {
    ...(d.id !== undefined ? { id: d.id } : {}),
    kind: d.kind,
    label: d.label.trim(),
    model: d.model.trim(),
    surfaces: d.surfaces,
    enabled: d.enabled,
  };
  if (d.kind === 'env') {
    base.baseUrl = d.baseUrl.trim();
    if (d.apiKey.trim() !== '') base.apiKey = d.apiKey.trim();
    else if (!d.keepKey) base.apiKey = '';
  } else {
    base.bin = d.bin.trim();
  }
  return base;
}

export function AiOpenCatalogSection(): JSX.Element {
  const [data, setData] = useState<AiCatalogResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [side, setSide] = useState<SideKey>('all');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchAiCatalog());
    } catch {
      setData(null);
      setError('목록을 불러오지 못했습니다.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const entries = useMemo(() => data?.catalog.entries ?? [], [data]);
  /** 판 J — 연결별 사용량. **줄이 없으면 「기록 없음」**이지 0 이 아니다. */
  const usageById = useMemo(() => {
    const m = new Map<string, AiUsageRow>();
    for (const u of data?.usage ?? []) m.set(u.entryId, u);
    return m;
  }, [data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (side !== 'all' && e.kind !== side) return false;
      if (q === '') return true;
      return [e.label, e.baseUrl, e.bin, e.model].some((v) => v.toLowerCase().includes(q));
    });
  }, [entries, query, side]);

  /** 저장은 목록 전체를 보낸다 — 서버가 통째로 다듬어 돌려준다. */
  const commit = useCallback(async (next: AiEntryDraft[], closeModal = true) => {
    setBusy(true);
    setError(null);
    try {
      const catalog = await saveAiCatalog(next);
      setData((prev) => (prev === null ? prev : { ...prev, catalog }));
      if (closeModal) setDraft(null);
      return true;
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? '저장하지 못했습니다.');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const saveDraft = useCallback(async (d: Draft) => {
    const payload = toPayload(d);
    const next = d.id !== undefined
      ? entries.map((e) => (e.id === d.id ? payload : untouched(e)))
      : [...entries.map(untouched), payload];
    await commit(next);
  }, [entries, commit]);

  /** 줄에서 바로 고치는 것들 — 자리·열림. 「사용도 정리할 수 있어야 한다」(사용자). */
  const patchEntry = useCallback(async (target: AiEntry, patch: Partial<AiEntryDraft>) => {
    const next = entries.map((e) => (e.id === target.id ? { ...untouched(e), ...patch } : untouched(e)));
    await commit(next, false);
  }, [entries, commit]);

  const toggleSurface = useCallback((e: AiEntry, s: AiSurface) => {
    const surfaces = e.surfaces.includes(s) ? e.surfaces.filter((x) => x !== s) : [...e.surfaces, s];
    /* ★ 마지막 자리를 끄면서 열려 있으면 서버가 막는다 — 미리 함께 닫아 준다. */
    void patchEntry(e, { surfaces, enabled: surfaces.length === 0 ? false : e.enabled });
  }, [patchEntry]);

  const toggleEnabled = useCallback((e: AiEntry) => {
    if (!e.enabled && e.surfaces.length === 0) {
      setError(`「${e.label}」을 열려면 쓸 자리(AI 메뉴·챗)를 하나 이상 골라 주세요.`);
      return;
    }
    void patchEntry(e, { enabled: !e.enabled });
  }, [patchEntry]);

  /**
   * 이 서버가 이미 아는 provider 를 목록에 올린다.
   *
   * ★★★ **가리키는 게 아니라 «값»을 담는다**(판 J). 서버가 backend 에서 주소·토큰을 받아
   *   암호화해 넣는다 — 토큰이 브라우저를 거치지 않는다.
   * ★ **닫힌 채로** 올린다. 무엇을 열지는 사람이 한 줄씩 정한다.
   */
  const runImport = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await importDetectedProviders();
      setNote(r.added === 0
        ? '이미 다 들어와 있습니다 — 새로 가져올 것이 없습니다.'
        : `${r.added}건을 가져왔습니다. 닫힌 채로 들어왔으니 쓸 자리를 고르고 열어 주세요.`);
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? '가져오지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const removeEntry = useCallback(async (e: AiEntry) => {
    await commit(entries.filter((x) => x.id !== e.id).map(untouched), false);
  }, [entries, commit]);

  const openCount = entries.filter((e) => e.enabled).length;
  /** 열려 있는데 자리가 없는 줄 — 옛 데이터에 있을 수 있다. 화면이 그것을 말한다. */
  const strandedCount = entries.filter((e) => e.enabled && e.surfaces.length === 0).length;

  return (
    <>
      <ConsoleListScreen
        title="AI 연결"
        kpi={`${entries.length}개 · 열림 ${openCount}`}
        desc="시스템 관리자가 여기서 연결을 등록·수정하고 «엽니다». 연 것만 회사 관리자(통합설정 › AI 연결)에게 보입니다. API 토큰은 저장만 되고 화면으로 다시 나오지 않습니다."
        search={{ value: query, onChange: setQuery, placeholder: '이름 · 주소 · 모델' }}
        side={{
          head: '종류',
          active: side,
          onChange: (k) => setSide(k as SideKey),
          items: [
            { key: 'all', label: '전체', count: entries.length },
            { key: 'env', label: KIND_LABEL.env, count: entries.filter((e) => e.kind === 'env').length },
            { key: 'cli', label: KIND_LABEL.cli, count: entries.filter((e) => e.kind === 'cli').length },
          ],
        }}
        toolsExtra={(
          <button
            type="button" className="svc-btn" disabled={busy}
            onClick={() => { void runImport(); }}
            title="이 서버가 아는 provider 의 주소·토큰을 받아 목록에 담습니다(닫힌 채)"
          >
            <Download size={14} aria-hidden="true" /> 기존 provider 가져오기
          </button>
        )}
        add={{ label: '연결 등록', onClick: () => setDraft(toDraft(null)), disabled: busy }}
        notice={(
          <>
            {error !== null && (
              <div className="admin-console__alert" role="alert">
                <AlertTriangle size={16} aria-hidden="true" /><span>{error}</span>
              </div>
            )}
            {note !== null && (
              <div className="admin-console__alert" role="status">
                <Check size={16} aria-hidden="true" /><span>{note}</span>
              </div>
            )}
            {strandedCount > 0 && (
              <div className="admin-console__alert" role="alert">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>
                  열려 있지만 쓸 자리를 안 고른 연결이 {strandedCount}건 있습니다 —
                  회사 관리자에게는 <b>보이지 않습니다</b>. 자리를 골라 주세요.
                </span>
              </div>
            )}
          </>
        )}
      >
        <ConsoleListTable<AiEntry>
          rows={rows}
          rowKey={(e) => e.id}
          onRowClick={(e) => setDraft(toDraft(e))}
          empty={<span>아직 만든 연결이 없습니다 — 회사 관리자에게는 고를 수 있는 것이 없습니다.</span>}
          columns={[
            { key: 'label', header: '이름', render: (e) => <span className="cl-name">{e.label}</span> },
            { key: 'kind', header: '종류', render: (e) => <span className="cl-badge">{KIND_LABEL[e.kind]}</span> },
            {
              key: 'conn',
              header: '연결',
              render: (e) => (e.kind === 'env'
                ? (
                  <span title={e.baseUrl}>
                    {e.baseUrl || '—'}
                    <span className="cl-chip">{e.hasApiKey ? `토큰 ${e.apiKeyHint}` : '토큰 없음'}</span>
                  </span>
                )
                : <span>{e.bin || '—'}</span>),
            },
            { key: 'model', header: '모델', render: (e) => <span>{e.model || '(기본)'}</span> },
            {
              key: 'surfaces',
              header: '쓸 수 있는 자리',
              /* ★ 목록에서 바로 켜고 끈다 — 「사용도 정리할 수 있어야 한다」(사용자). */
              render: (e) => (
                <span onClick={(ev) => ev.stopPropagation()}>
                  {ALL_SURFACES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      /* ★ 수식은 이 규격이 쓰던 `is-on` 을 그대로 쓴다 — 새 이름을 지어내면 조용히 죽는다. */
                      className={e.surfaces.includes(s) ? 'cl-chip is-on' : 'cl-chip'}
                      aria-pressed={e.surfaces.includes(s)}
                      aria-label={`${e.label} ${SURFACE_LABEL[s]} ${e.surfaces.includes(s) ? '끄기' : '켜기'}`}
                      disabled={busy}
                      onClick={() => toggleSurface(e, s)}
                    >
                      {SURFACE_LABEL[s]}
                    </button>
                  ))}
                </span>
              ),
            },
            {
              key: 'usage',
              header: '사용 (7일)',
              render: (e) => {
                const u = usageById.get(e.id);
                /* ★ 「기록 없음」과 「0」은 다르다 — 안 쓴 것과 못 잰 것을 같은 말로 적지 않는다. */
                if (!u) return <span className="cl-empty">기록 없음</span>;
                return (
                  <span title={`호출 ${u.calls}회 · 마지막 ${u.lastDay ?? '—'}`}>
                    {fmtTokens(u.promptTokens + u.completionTokens)}
                    {u.todayTokens > 0 && <span className="cl-chip">오늘 {fmtTokens(u.todayTokens)}</span>}
                  </span>
                );
              },
            },
            {
              key: 'enabled',
              header: '상태',
              render: (e) => (
                <span onClick={(ev) => ev.stopPropagation()}>
                  <button
                    type="button"
                    className={e.enabled ? 'cl-badge is-on' : 'cl-badge'}
                    aria-pressed={e.enabled}
                    aria-label={`${e.label} ${e.enabled ? '닫기' : '열기'}`}
                    disabled={busy}
                    onClick={() => toggleEnabled(e)}
                  >
                    {e.enabled ? '열림' : '닫힘'}
                  </button>
                </span>
              ),
            },
          ]}
          rowActions={(e) => (
            <>
              <button
                type="button"
                className="cl-rowbtn"
                aria-label={`${e.label} 수정`}
                onClick={(ev) => { ev.stopPropagation(); setDraft(toDraft(e)); }}
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="cl-rowbtn cl-rowbtn--danger"
                aria-label={`${e.label} 삭제`}
                disabled={busy}
                onClick={(ev) => { ev.stopPropagation(); void removeEntry(e); }}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </>
          )}
        />
      </ConsoleListScreen>

      {draft !== null && (
        <EntryModal
          draft={draft}
          detected={data?.detected ?? { env: [], cli: [] }}
          usage={draft.id !== undefined ? usageById.get(draft.id) ?? null : null}
          busy={busy}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSave={() => { void saveDraft(draft); }}
        />
      )}
    </>
  );
}

function EntryModal({
  draft, detected, usage, busy, onChange, onClose, onSave,
}: {
  draft: Draft;
  detected: AiCatalogResult['detected'];
  usage: AiUsageRow | null;
  busy: boolean;
  onChange: (d: Draft) => void;
  onClose: () => void;
  onSave: () => void;
}): JSX.Element {
  const set = (p: Partial<Draft>): void => onChange({ ...draft, ...p });
  const toggleSurface = (s: AiSurface): void => set({
    surfaces: draft.surfaces.includes(s) ? draft.surfaces.filter((x) => x !== s) : [...draft.surfaces, s],
  });

  /** 판 J — 「모델도 확인이 가능해야 한다」(사용자). 적어 둔 주소·토큰으로 실제로 물어본다. */
  const [models, setModels] = useState<string[] | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeErr, setProbeErr] = useState<string | null>(null);

  const probe = async (): Promise<void> => {
    setProbing(true);
    setProbeErr(null);
    try {
      const r = await probeAiModels({
        ...(draft.id !== undefined ? { entryId: draft.id } : {}),
        baseUrl: draft.baseUrl.trim(),
        /* 저장된 것을 쓸 때는 토큰을 안 보낸다 — 서버가 꺼내 쓴다. */
        ...(draft.apiKey.trim() !== '' ? { apiKey: draft.apiKey.trim() } : {}),
      });
      if (r.ok && r.models) {
        setModels(r.models);
        if (r.models.length === 0) setProbeErr('이 주소는 모델 목록을 주지 않습니다 — 직접 적어 주세요.');
      } else {
        setProbeErr(r.error ?? '모델 목록을 못 받았습니다.');
      }
    } catch {
      setProbeErr('모델 목록을 못 받았습니다.');
    } finally {
      setProbing(false);
    }
  };

  const cantOpen = draft.enabled && draft.surfaces.length === 0;

  return (
    <div className="cl-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="cl-modal"
        role="dialog"
        aria-modal="true"
        aria-label={draft.id !== undefined ? '연결 수정' : '연결 등록'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cl-modal__head">
          <span>{draft.id !== undefined ? '연결 수정' : '연결 등록'}</span>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="cl-modal__body">
          <label className="cl-field">
            <span>종류</span>
            <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as AiEntryKind })}>
              <option value="env">{KIND_LABEL.env} — 주소와 토큰을 여기서 정한다</option>
              <option value="cli">{KIND_LABEL.cli} — 이 서버의 실행 파일을 쓴다</option>
            </select>
          </label>

          <label className="cl-field">
            <span>이름</span>
            <input
              value={draft.label}
              onChange={(e) => set({ label: e.target.value })}
              placeholder="회사 관리자에게 보일 이름"
            />
          </label>

          {draft.kind === 'env' ? (
            <>
              <label className="cl-field">
                <span>API 주소</span>
                <input
                  value={draft.baseUrl}
                  onChange={(e) => set({ baseUrl: e.target.value })}
                  placeholder="https://…/v1"
                  list="ai-detected-env"
                />
              </label>
              <datalist id="ai-detected-env">
                {detected.env.map((d) => <option key={d.id} value={d.base_url ?? ''}>{d.name}</option>)}
              </datalist>

              <label className="cl-field">
                <span>API 토큰</span>
                <input
                  type="password"
                  value={draft.apiKey}
                  onChange={(e) => set({ apiKey: e.target.value })}
                  placeholder={draft.hasApiKey
                    ? `저장돼 있음 (${draft.apiKeyHint ?? ''}) — 바꿀 때만 입력`
                    : '토큰을 붙여 넣으세요'}
                  autoComplete="new-password"
                />
              </label>
              {/* ★ 토큰은 원문이 오지 않는다 — 비워 두면 「그대로 둔다」는 뜻임을 화면이 말해야 한다. */}
              {draft.hasApiKey && (
                <label className="cl-check">
                  <input
                    type="checkbox"
                    checked={!draft.keepKey}
                    onChange={(e) => set({ keepKey: !e.target.checked })}
                  />
                  <span>저장된 토큰 지우기</span>
                </label>
              )}
            </>
          ) : (
            <>
              <label className="cl-field">
                <span>실행 파일</span>
                <input
                  value={draft.bin}
                  onChange={(e) => set({ bin: e.target.value })}
                  placeholder="claude"
                  list="ai-detected-cli"
                />
              </label>
              <datalist id="ai-detected-cli">
                {detected.cli.map((d) => <option key={d.bin} value={d.bin}>{d.name}</option>)}
              </datalist>
              <p className="cl-empty">
                이 서버에서 감지된 것: {detected.cli.map((d) => d.bin).join(' · ') || '없음'}
              </p>
            </>
          )}

          <label className="cl-field">
            <span>모델</span>
            <input
              value={draft.model}
              onChange={(e) => set({ model: e.target.value })}
              placeholder="비우면 그 연결의 기본 모델"
              list="ai-probed-models"
            />
          </label>
          {models !== null && (
            <datalist id="ai-probed-models">
              {models.map((m) => <option key={m} value={m} />)}
            </datalist>
          )}
          {draft.kind === 'env' && (
            <p className="cl-empty">
              <button type="button" className="svc-btn" disabled={probing} onClick={() => { void probe(); }}>
                <RefreshCw size={14} aria-hidden="true" /> {probing ? '물어보는 중…' : '모델 불러오기'}
              </button>
              {models !== null && models.length > 0 && ` — ${models.length}개를 찾았습니다. 칸을 누르면 고를 수 있습니다.`}
              {probeErr !== null && ` — ${probeErr}`}
            </p>
          )}

          <div className="cl-field">
            <span>쓸 수 있는 자리</span>
            <div>
              {ALL_SURFACES.map((s) => (
                <label key={s} className="cl-check">
                  <input
                    type="checkbox"
                    checked={draft.surfaces.includes(s)}
                    onChange={() => toggleSurface(s)}
                  />
                  <span>{SURFACE_LABEL[s]}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="cl-check">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <span>회사 관리자에게 열기</span>
          </label>
          <p className="cl-empty">닫으면 이미 이것을 고른 회사도 즉시 운영자 기본으로 떨어집니다.</p>
          {/* ★★★ 「열었는데 아무 데도 안 나온다」를 미리 말린다. */}
          {cantOpen && (
            <div className="admin-console__alert" role="alert">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>열려면 쓸 자리를 하나 이상 골라야 합니다 — 그러지 않으면 회사 화면에 안 보입니다.</span>
            </div>
          )}

          {/* 판 J — 이 연결이 최근에 얼마나 쓰였나. */}
          {usage !== null && (
            <div className="cl-field">
              <span>최근 7일</span>
              <div>
                <p className="cl-empty">
                  토큰 {fmtTokens(usage.promptTokens + usage.completionTokens)} · 호출 {usage.calls}회
                  {usage.lastDay !== null && ` · 마지막 ${usage.lastDay}`}
                </p>
                {usage.daily.map((d) => (
                  <p key={d.day} className="cl-empty">
                    {d.day} — {fmtTokens(d.tokens)} 토큰 · {d.calls}회
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="cl-modal__foot">
          <button type="button" className="svc-btn" onClick={onClose}>취소</button>
          <button type="button" className="svc-btn svc-btn--primary" onClick={onSave} disabled={busy || cantOpen}>
            <Check size={14} aria-hidden="true" /> {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
